import type { Level, PollVote } from "./types";

export const ALPH = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
export const MAX_BODY = 64 * 1024;
export const HOUR_MS = 60 * 60 * 1000;
export const MAX_PARTICIPANTS = 60;
export const JOIN_LIMIT = 60;

export const SECURITY_HEADERS: Record<string, string> = {
  "strict-transport-security": "max-age=31536000; includeSubDomains",
  "x-content-type-options": "nosniff",
  "referrer-policy": "no-referrer",
};

export const API_HEADERS: Record<string, string> = {
  "content-type": "application/json; charset=utf-8",
  "cache-control": "no-store",
  ...SECURITY_HEADERS,
};

export function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

export function randomChars(len: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(len));
  let s = "";
  for (let i = 0; i < len; i++) {
    const b = bytes[i] ?? 0;
    s += ALPH[b & 63];
  }
  return s;
}

export function json(data: unknown, status = 200, extra: Record<string, string> = {}): Response {
  return new Response(JSON.stringify(data), {
    status,
    headers: { ...API_HEADERS, ...extra },
  });
}

export function clientIp(request: Request): string {
  return request.headers.get("CF-Connecting-IP") ?? "unknown";
}

export function isCrossSite(request: Request): boolean {
  const origin = request.headers.get("Origin");
  if (origin === null || origin === "") return false;
  try {
    return origin !== new URL(request.url).origin;
  } catch {
    return true;
  }
}

export function hasJsonBody(request: Request): boolean {
  const ct = request.headers.get("content-type");
  if (ct === null) return false;
  return ct.toLowerCase().startsWith("application/json");
}

export function saveLimitKey(eventId: string, editHash: string | null, ip: string): string {
  return `${eventId}:${editHash ?? "ip:" + ip}`;
}

/** Constant-time compare. An early return on the first mismatch is behaviorally identical, so this property cannot be expressed as a test. */
export function hexEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let out = 0;
  for (let i = 0; i < a.length; i++) {
    out |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return out === 0;
}

export async function sha256Hex(s: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function readTextCapped(res: Response, maxBytes: number): Promise<string | null> {
  const cl = res.headers.get("content-length");
  if (cl !== null) {
    const n = Number(cl);
    if (!Number.isFinite(n) || n > maxBytes) return null;
  }
  const body = res.body;
  if (!body) return "";
  const reader = body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  const capPlus = maxBytes + 1;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value || value.byteLength === 0) continue;
    const room = capPlus - total;
    if (value.byteLength > room) {
      chunks.push(value.slice(0, room));
      total += room;
      await reader.cancel();
      return null;
    }
    chunks.push(value);
    total += value.byteLength;
    if (total > maxBytes) {
      await reader.cancel();
      return null;
    }
  }
  const buf = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) {
    buf.set(c, offset);
    offset += c.byteLength;
  }
  return new TextDecoder().decode(buf);
}

export async function readJsonCapped(
  request: Request,
  max = MAX_BODY,
): Promise<{ ok: true; value: unknown } | { ok: false; status: 400 | 413; error: string }> {
  const cl = request.headers.get("content-length");
  if (cl !== null) {
    const n = Number(cl);
    if (!Number.isFinite(n) || n > max) {
      return { ok: false, status: 413, error: "body too large" };
    }
  }
  const body = request.body;
  if (!body) {
    return { ok: false, status: 400, error: "invalid json" };
  }
  const reader = body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  const capPlus = max + 1;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value || value.byteLength === 0) continue;
    const room = capPlus - total;
    if (value.byteLength > room) {
      chunks.push(value.slice(0, room));
      total += room;
      await reader.cancel();
      return { ok: false, status: 413, error: "body too large" };
    }
    chunks.push(value);
    total += value.byteLength;
    if (total > max) {
      await reader.cancel();
      return { ok: false, status: 413, error: "body too large" };
    }
  }
  const buf = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) {
    buf.set(c, offset);
    offset += c.byteLength;
  }
  try {
    return { ok: true, value: JSON.parse(new TextDecoder().decode(buf)) as unknown };
  } catch {
    return { ok: false, status: 400, error: "invalid json" };
  }
}

export function allowWithin(
  map: Map<string, number[]>,
  key: string,
  nowMs: number,
  limit: number,
  windowMs: number,
): boolean {
  const times = map.get(key) ?? [];
  let live = 0;
  for (const t of times) {
    if (nowMs - t < windowMs) live += 1;
  }
  return live < limit;
}

export function allowJoin(map: Map<string, number[]>, ip: string, nowMs: number): boolean {
  return allowWithin(map, ip, nowMs, JOIN_LIMIT, HOUR_MS);
}

export function recordHit(
  map: Map<string, number[]>,
  key: string,
  nowMs: number,
  windowMs: number,
  maxKeys: number,
): void {
  const prev = map.get(key) ?? [];
  const live = prev.filter((t) => nowMs - t < windowMs);
  live.push(nowMs);
  map.set(key, live);
  while (map.size > maxKeys) {
    const oldest = map.keys().next().value;
    if (oldest === undefined) break;
    map.delete(oldest);
  }
}

export function touchHourlyMap(
  map: Map<string, number[]>,
  key: string,
  now: number,
  limit: number,
  maxKeys = 10_000,
): boolean {
  if (!allowWithin(map, key, now, limit, HOUR_MS)) return false;
  recordHit(map, key, now, HOUR_MS, maxKeys);
  return true;
}

/** A token bucket. `tokens` may be fractional between refills; `at` is the epoch-ms of the last
 * observation. */
export interface Bucket {
  tokens: number;
  at: number;
}

/** Pure token bucket: refills `refillPerSec` tokens per second up to `capacity`, starting full
 * when `b` is undefined (a room's first write). Never mutates `b`; the caller stores the returned
 * bucket for the next call. */
export function takeToken(
  b: Bucket | undefined,
  nowMs: number,
  capacity: number,
  refillPerSec: number,
): { ok: boolean; bucket: Bucket } {
  const prev = b ?? { tokens: capacity, at: nowMs };
  const elapsedSec = Math.max(0, (nowMs - prev.at) / 1000);
  const refilled = Math.min(capacity, prev.tokens + elapsedSec * refillPerSec);
  if (refilled < 1) {
    return { ok: false, bucket: { tokens: refilled, at: nowMs } };
  }
  return { ok: true, bucket: { tokens: refilled - 1, at: nowMs } };
}

/** Value-equality for an availability map: a key absent from either side counts as level 0 (the
 * server never stores a 0), so insertion order and an omitted zero never register as a change.
 * Shared by the no-op-save check (src/shared/room.ts) and the cross-tab merge (src/shared/sync.ts)
 * so the two places that need "same availability" agree on what that means. */
export function levelsEqual(a: Record<string, Level>, b: Record<string, Level>): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    if ((a[k] ?? 0) !== (b[k] ?? 0)) return false;
  }
  return true;
}

/** Value-equality for a pollId -> optionId -> vote map. Unlike a Level, a stored PollVote of 0
 * ("No") is a real answer, distinct from an absent key ("never voted"), so a missing side is
 * compared as `undefined` rather than defaulted to 0. */
export function pollVotesEqual(
  a: Record<string, Record<string, PollVote>>,
  b: Record<string, Record<string, PollVote>>,
): boolean {
  const pollIds = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const pollId of pollIds) {
    const av = a[pollId] ?? {};
    const bv = b[pollId] ?? {};
    const optIds = new Set([...Object.keys(av), ...Object.keys(bv)]);
    for (const optId of optIds) {
      if (av[optId] !== bv[optId]) return false;
    }
  }
  return true;
}
